import { tokens } from "../theme";
import { forwardRef, useEffect, useState } from "react";
import { Box, IconButton, Tooltip, useTheme } from "@mui/material";
import { Close, Fullscreen, FullscreenExit } from "@mui/icons-material";
import useResizableHeight from "./useResizableHeight";
import useResizableWidth from "./useResizableWidth";
import FrameResizeHandle from "./FrameResizeHandle";
import FrameWidthResizeHandle from "./FrameWidthResizeHandle";

const styles = (colors) => ({
  // A row: the column below (toolbar/frame/height-handle) plus the optional
  // width-handle strip to its right.
  root: {
    display: "flex",
    flexDirection: "row",
    minHeight: 0,
  },
  // Same Box, same DOM node as the non-maximized state — only its own
  // position/size change here, so the <iframe> underneath is never
  // unmounted/remounted by toggling this (scroll position and any in-frame
  // navigation survive). Needs no ancestor with transform/filter/contain:
  // those trap position:fixed to their own box instead of the viewport, so a
  // caller centring this inside a Modal should use flex centring, not
  // transform.
  maximizedOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: 1300, // MUI's modal tier, so this wins over app chrome (sidebar, headers, other modals below it)
    width: "100vw",
    height: "100vh",
  },
  column: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  },
  toolbar: {
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "2px",
    px: "6px",
    py: "4px",
    backgroundColor: colors.surface,
    borderBottom: `1px solid ${colors.border}`,
  },
  toolbarButton: {
    color: colors.sec,
    "&:hover": { backgroundColor: colors.divider, color: colors.body },
  },
  frameArea: {
    position: "relative",
    flex: 1,
    minHeight: 0,
  },
  iframe: {
    display: "block",
    width: "100%",
    height: "100%",
    border: 0,
    background: colors.surface,
  },
});

// Common embedded-iframe viewer: Jira/Grafana reports, logs, or any other
// external page shown inside the app. Every such iframe in the app should go
// through this one component rather than a raw <iframe>, so the Maximize
// behaviour (and any future iframe-hygiene fix) lives in exactly one place.
//
// Uncontrolled by default (owns its own `maximized` state, toggled by its own
// button and by Escape). Pass `maximized` + `onMaximizedChange` to lift that
// state instead — e.g. a page that wants to persist it, or react to it itself.
//
// `sx` sizes the non-maximized footprint (the caller decides that; there is
// no sane default). `iframeKey`, if given, becomes the <iframe>'s React key —
// changing it forces a real reload, independent of the maximize toggle
// (which must never itself force one). `overlay` renders on top of the frame
// (a loading spinner, a "didn't load" fallback, ...). `toolbarStart` renders
// before the Maximize button in the toolbar (e.g. a Reload action).
// `closable` + `onClose` add a Close button, for embedding inside a modal.
//
// `resizable` adds a drag handle on the bottom edge (grow/shrink height) and
// `resizableWidth` one on the right edge (grow/shrink width) — independent
// toggles, so a caller can offer either, both, or neither. Each needs its own
// `resizeStorageKey`/`widthResizeStorageKey` (unique per usage, persists the
// chosen size the same way drawer widths persist) and optionally
// `defaultHeight`/`minHeight`/`maxHeight` or `defaultWidth`/`minWidth`/
// `maxWidth`. Both are ignored while maximized — already fullscreen, nothing
// to resize.
const IframeViewer = forwardRef(
  (
    {
      src,
      title,
      sx,
      iframeKey,
      onLoad,
      referrerPolicy,
      allow,
      allowFullScreen,
      maximized: maximizedProp,
      onMaximizedChange,
      toolbarStart,
      closable = false,
      onClose,
      overlay,
      resizable = false,
      resizeStorageKey = "iframe-viewer",
      defaultHeight = 480,
      minHeight,
      maxHeight = 1000,
      resizableWidth = false,
      widthResizeStorageKey = "iframe-viewer",
      defaultWidth = 800,
      minWidth,
      maxWidth = 1200,
    },
    ref
  ) => {
    const theme = useTheme();
    const colors = tokens(theme.paletteId);
    const s = styles(colors);
    const [internalMaximized, setInternalMaximized] = useState(false);
    const isControlled = maximizedProp !== undefined;
    const maximized = isControlled ? maximizedProp : internalMaximized;

    const setMaximized = (next) => {
      onMaximizedChange?.(next);
      if (!isControlled) setInternalMaximized(next);
    };

    const { height: resizedHeight, handleProps: heightHandleProps } =
      useResizableHeight({
        storageKey: resizeStorageKey,
        defaultHeight,
        minHeight,
        maxHeight,
        enabled: resizable && !maximized,
      });

    const { width: resizedWidth, handleProps: widthHandleProps } =
      useResizableWidth({
        storageKey: widthResizeStorageKey,
        defaultWidth,
        minWidth,
        maxWidth,
        enabled: resizableWidth && !maximized,
      });

    useEffect(() => {
      if (!maximized) return undefined;
      const onKeyDown = (event) => {
        if (event.key !== "Escape") return;
        onMaximizedChange?.(false);
        if (!isControlled) setInternalMaximized(false);
      };
      window.addEventListener("keydown", onKeyDown);
      return () => window.removeEventListener("keydown", onKeyDown);
    }, [maximized, isControlled, onMaximizedChange]);

    return (
      <Box
        ref={ref}
        sx={[
          s.root,
          sx,
          resizable ? { height: `${resizedHeight}px` } : null,
          resizableWidth ? { width: `${resizedWidth}px` } : null,
          maximized ? s.maximizedOverlay : null,
        ]}
      >
        <Box sx={s.column}>
          <Box sx={s.toolbar}>
            {toolbarStart}
            <Tooltip title={maximized ? "Restore" : "Maximize"}>
              <IconButton
                size="small"
                sx={s.toolbarButton}
                onClick={() => setMaximized(!maximized)}
              >
                {maximized ? (
                  <FullscreenExit fontSize="small" />
                ) : (
                  <Fullscreen fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
            {closable ? (
              <Tooltip title="Close">
                <IconButton size="small" sx={s.toolbarButton} onClick={onClose}>
                  <Close fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
          </Box>
          <Box sx={s.frameArea}>
            <iframe
              key={iframeKey}
              title={title}
              src={src}
              style={s.iframe}
              onLoad={onLoad}
              referrerPolicy={referrerPolicy}
              allow={allow}
              allowFullScreen={allowFullScreen}
            />
            {overlay}
          </Box>
          {resizable ? (
            <FrameResizeHandle
              {...heightHandleProps}
              ariaLabel="Resize iframe height"
            />
          ) : null}
        </Box>
        {resizableWidth ? (
          <FrameWidthResizeHandle
            {...widthHandleProps}
            ariaLabel="Resize iframe width"
          />
        ) : null}
      </Box>
    );
  }
);

IframeViewer.displayName = "IframeViewer";

export default IframeViewer;
